<script setup>
// The first step of every sign-in / sign-up surface (ACCOUNT_IDENTITY_PLAN.md layer 0):
// two equal cards, no default. Each card is an accordion header — the chosen one opens
// with its form inside, the other stays visible as the way across. The chosen side is the
// intent every button inside then carries ("Sign in with Apple" vs "Sign up with Apple"),
// so a wrong guess can no longer create an account.
const props = defineProps({
  modelValue: {
    type: String, // 'signIn' | 'signUp' | null
    default: null,
  },
  // Optional context line, e.g. the club/meet an invite is for.
  context: {
    type: String,
    default: null,
  },
});
const emit = defineEmits(['update:modelValue']);
const choose = (v) => emit('update:modelValue', props.modelValue === v ? null : v);
</script>

<template>
  <div class="auth-choice" data-testid="auth-chooser">
    <div v-if="context" class="auth-choice-context">{{ context }}</div>

    <div class="auth-choice-card" :class="{on: modelValue === 'signUp', off: modelValue === 'signIn'}">
      <button type="button" class="auth-choice-head" data-testid="auth-choose-signup"
              :aria-expanded="modelValue === 'signUp'" @click="choose('signUp')">
        <span class="auth-choice-icon"><v-icon size="22">mdi-account-plus-outline</v-icon></span>
        <span class="auth-choice-text">
          <span class="auth-choice-title">Create an account</span>
          <span class="auth-choice-sub">I've never used Tap Leagues before.</span>
        </span>
        <v-icon size="22" class="auth-choice-chev" :class="{open: modelValue === 'signUp'}">mdi-chevron-down</v-icon>
      </button>
      <v-expand-transition>
        <div v-if="modelValue === 'signUp'" class="auth-choice-body" data-testid="auth-body-signup">
          <slot name="signUp"/>
        </div>
      </v-expand-transition>
    </div>

    <div class="auth-choice-card" :class="{on: modelValue === 'signIn', off: modelValue === 'signUp'}">
      <button type="button" class="auth-choice-head" data-testid="auth-choose-signin"
              :aria-expanded="modelValue === 'signIn'" @click="choose('signIn')">
        <span class="auth-choice-icon"><v-icon size="22">mdi-login-variant</v-icon></span>
        <span class="auth-choice-text">
          <span class="auth-choice-title">Sign in</span>
          <span class="auth-choice-sub">I already have a Tap Leagues account.</span>
        </span>
        <v-icon size="22" class="auth-choice-chev" :class="{open: modelValue === 'signIn'}">mdi-chevron-down</v-icon>
      </button>
      <v-expand-transition>
        <div v-if="modelValue === 'signIn'" class="auth-choice-body" data-testid="auth-body-signin">
          <slot name="signIn"/>
        </div>
      </v-expand-transition>
    </div>

    <div v-if="!modelValue" class="auth-choice-note">Joining another club? Use the account you already have. One account works for every club.</div>
  </div>
</template>

<style scoped lang="scss">
.auth-choice {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.auth-choice-context {
  font-size: 12.5px;
  color: rgb(var(--v-theme-charcoal));
  margin-bottom: 2px;
}

.auth-choice-note {
  font-size: 11.5px;
  line-height: 1.45;
  color: rgb(var(--v-theme-textSubtle));
  text-align: center;
  margin-top: 2px;
}

.auth-choice-card {
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 14px;
  background: rgb(var(--v-theme-surface));
  overflow: hidden;
  transition: border-color .15s, box-shadow .15s, opacity .15s;

  // Open: the tinted header, rotated chevron and the form itself say which
  // card is active — no outline needed.

  &.off {
    opacity: .8;
  }
}

.auth-choice-head {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 14px 14px 14px 12px;
  background: transparent;
  border: 0;
  cursor: pointer;

  &:hover, &:focus-visible {
    background: rgba(var(--v-theme-primary), .03);
    outline: none;
  }

  .on > & {
    background: rgba(var(--v-theme-primary), .04);
  }
}

.auth-choice-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  flex-shrink: 0;
  background: rgba(var(--v-theme-primary), .08);
  color: rgb(var(--v-theme-primary));
}

.auth-choice-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.auth-choice-title {
  font-size: 15px;
  font-weight: 800;
  letter-spacing: -0.2px;
  color: rgb(var(--v-theme-text));
}

.auth-choice-sub {
  font-size: 12px;
  line-height: 1.45;
  color: rgb(var(--v-theme-charcoal));
}

.auth-choice-chev {
  color: rgb(var(--v-theme-textSubtle));
  flex-shrink: 0;
  transition: transform .18s;

  &.open {
    transform: rotate(180deg);
    color: rgb(var(--v-theme-primary));
  }
}

.auth-choice-body {
  padding: 16px 14px 16px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}
</style>
